import { useRouteError } from 'react-router'

/**
 * Écran de dernier recours quand une route tombe.
 *
 * Porté depuis `src/app/global-error.tsx` (Next) pour que les deux frontends
 * échouent de la même façon. Sans lui, React Router affichait sa propre page
 * de développement : un fond noir, la pile d'appels minifiée et un « Hey
 * developer 👋 ». C'est ce qu'un client a eu sous les yeux le 22 août.
 *
 * Deux écarts assumés avec la version Next :
 *
 * Les textes sont en dur plutôt que via i18next. Cet écran s'affiche
 * précisément quand quelque chose est cassé — si la panne vient d'i18next ou
 * de son chargement, un `t()` échouerait à son tour et on perdrait le seul
 * message que l'utilisateur devait lire. La locale est lue dans l'URL, comme
 * côté Next.
 *
 * Les styles sont en ligne, sans Tailwind ni composant maison, pour la même
 * raison : ne dépendre de rien de ce qui a pu tomber.
 */

const TEXTES = {
  fr: {
    titre: "Oups, quelque chose n'a pas fonctionné",
    message: "L'affichage a rencontré un problème. Rafraîchissez la page pour reprendre.",
    rassurance: 'Vos appels et vos messages continuent normalement. Seul l’affichage est touché.',
    rafraichir: 'Rafraîchir la page',
    connexion: 'Se reconnecter',
  },
  en: {
    titre: 'Oops, something went wrong',
    message: 'The interface ran into a problem. Refresh the page to carry on.',
    rassurance: 'Your calls and messages are still running. Only the display is affected.',
    rafraichir: 'Refresh the page',
    connexion: 'Sign in again',
  },
} as const

function locale(): 'fr' | 'en' {
  if (typeof window === 'undefined') return 'fr'
  return window.location.pathname.split('/')[1] === 'en' ? 'en' : 'fr'
}

export function AppErrorPage() {
  const erreur = useRouteError()
  const lang = locale()
  const t = TEXTES[lang]

  // La console garde la trace complète : PostHog l'enregistre, et le support
  // en a besoin. L'utilisateur, lui, n'a rien à faire d'une pile d'appels.
  if (erreur) console.error('[AppError]', erreur)

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0A192F',
        color: '#fff',
        padding: 24,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 440 }}>
        <div style={{ marginBottom: 16 }}>
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#f87171"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ display: 'inline-block' }}
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h1
          style={{
            fontSize: 22,
            fontWeight: 600,
            marginBottom: 8,
            color: '#f1f5f9',
          }}
        >
          {t.titre}
        </h1>

        <p
          style={{
            fontSize: 15,
            color: '#94a3b8',
            lineHeight: 1.6,
            marginBottom: 16,
          }}
        >
          {t.message}
        </p>

        {/* Un standard téléphonique qui affiche une erreur laisse croire que
            la ligne est coupée. Elle ne l'est pas : le routage vit dans le
            backend, indépendant du navigateur. C'est l'information que
            personne ne peut deviner, donc la plus utile de cet écran. */}
        <p
          style={{
            fontSize: 14,
            color: '#4ade80',
            lineHeight: 1.6,
            marginBottom: 24,
            background: 'rgba(74, 222, 128, 0.08)',
            border: '1px solid rgba(74, 222, 128, 0.2)',
            borderRadius: 8,
            padding: '10px 14px',
          }}
        >
          {t.rassurance}
        </p>

        <div
          style={{
            display: 'flex',
            gap: 12,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px',
              borderRadius: 8,
              border: 'none',
              background: '#3b82f6',
              color: '#fff',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {t.rafraichir}
          </button>
          <button
            onClick={() => {
              window.location.href = `/${lang}/login`
            }}
            style={{
              padding: '10px 24px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.15)',
              background: 'transparent',
              color: '#cbd5e1',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {t.connexion}
          </button>
        </div>
      </div>
    </div>
  )
}
